import { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { Collapsible } from 'src/components/ui/collapsible';
import { useGetAccountsQuery } from 'src/services/accountsService';
import { useCheckoutSessionMutation, useGetPaymentMethodsQuery } from 'src/services';
import { getEnvironment } from 'src/env/env';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAppSelector, useAppDispatch } from 'src/store';
import { useNavigate } from 'react-router-dom';
import { setSuccess } from 'src/store/slices/onboardingSlice';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { ProductsType } from 'src/constants/products';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const SaveCardOnboarding = ({ open = false, onButtonClick = () => {} }) => {
  const [stripe, setStripe] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isOpen, setIsOpen] = useState(open);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: accounts, isFetching: isFetchingAccounts } = useGetAccountsQuery();
  const [checkoutSession, { data: dataCheckoutSession, isLoading: isLoadingCheckoutSession }] =
    useCheckoutSessionMutation();
  const { project } = useAppSelector((s) => s.onboardingSlice);
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } = useGetPaymentMethodsQuery(
    {},
    { refetchOnMountOrArgChange: false },
  );

  useEffect(() => {
    if (paymentMethods?.data?.length) {
      onComplete();
    }
  }, [paymentMethods]);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (!stripe) {
      (async () => {
        const { APP_STRIPE_PUBLIC_KEY } = await getEnvironment();
        const stripeObject = await loadStripe(APP_STRIPE_PUBLIC_KEY);

        setStripe(stripeObject);
      })();
    }
  }, [stripe]);

  useLoaderWhile(isFetchingAccounts, isLoadingCheckoutSession, isLoadingPaymentMethods);

  useEffect(() => {
    if (accounts?.general_plan?.plan.id) {
      setPlanId(accounts.general_plan.plan.id);
    }
  }, [accounts]);

  useEffect(() => {
    if (planId) {
      checkoutSession({ plan_id: planId });
    }
  }, [planId]);

  useEffect(() => {
    if (dataCheckoutSession?.clientSecret) {
      setClientSecret(dataCheckoutSession.clientSecret);
    }
  }, [dataCheckoutSession]);

  const onComplete = () => {
    dispatch(setSuccess(true));

    if (project?.id) {
      navigate(
        `${PATH_DASHBOARD.PROJECTS}/${project?.id}/control-desk?type=${ProductsType.customer}`,
        { replace: true },
      );
    } else {
      navigate(`${PATH_DASHBOARD.ROOT}`);
    }
  };

  const onClick = () => {
    setIsOpen(true);
    onButtonClick();
  };

  return (
    <div
      className={
        'w-full max-w-xl flex flex-col gap-2.5 border border-green-default rounded-sm-5 shadow-[0_4px_70px_0_#E5FEF4] px-10 pt-10 pb-9'
      }
    >
      <div className={'w-full flex flex-col gap-1.5'}>
        <h2 className={'text-ebony text-xl font-bold leading-[130%] text-center capitalize'}>
          Save card to activate trial
        </h2>
        <p className={'text-ebony_opacity_70 text-sm font-medium text-center'}>
          We ask for a card to stop spam and keep the platform stable.
        </p>
      </div>
      <Collapsible open={isOpen} contentClassName={''}>
        <div>
          {stripe && clientSecret && (
            <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret, onComplete }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
      </Collapsible>
      {!isOpen && (
        <div className={'w-full flex items-center justify-center'}>
          <Button
            variant="bright-green"
            size="xl"
            className={'w-full max-w-[500px] text-sm font-semibold'}
            onClick={onClick}
          >
            Activate Free Trial
          </Button>
        </div>
      )}
      <div>
        <p className={'text-base-xs text-ebony_opacity_30 text-center font-medium'}>
          No charges today. <span className={'text-[#21DB5F]'}>Cancel anytime.</span>
        </p>
      </div>
    </div>
  );
};
